import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Table, Button, Space, Tag, Select, Typography, message, Popconfirm, Switch, Input, Row, Col, EmptyState, StatCard, EntityAvatar } from '../../ui';
import { Plus, Trash2, User, Star, Warehouse as WarehouseIcon, Phone, TriangleAlert, UserCheck, Mail, MapPin, CircleCheck, CircleSlash } from 'lucide-react';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { RoleBadge } from '../../components/RoleBadge';
import { MasterTag, warehouseOption } from '../../lib/warehouseLabel';
import { STATE_NAME } from '../../constants/indianStates';
import { formatIstRelative } from '../../lib/time';
import { useServerSort } from '../../hooks/useServerSort';

interface Warehouse { id: string; name: string; code: string; isMaster?: boolean }
interface Assignment {
  id: string; userId: string; warehouseId: string;
  userName?: string | null; userMobile?: string | null;
  warehouseName?: string | null; warehouseCode?: string | null;
  isPrimary: boolean; isActive: boolean; note?: string | null;
}

/**
 * Admin page to bind Warehouse Managers to the warehouses they operate (gated by
 * WAREHOUSE.MANAGER.ASSIGN). A WM is then scoped to those warehouses for stock, transfers,
 * packing and dispatch (WarehouseScopeService on the backend).
 */
export function WarehouseManagersPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [warehouseId, setWarehouseId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [isPrimary, setIsPrimary] = useState(false);
  const [note, setNote] = useState('');

  // Sorting is the backend's ORDER BY (see useServerSort). Each of the stacked cells sorts by the
  // field it leads with, so the column key and the backend field differ.
  const { sortState, onSortChange, sort } = useServerSort({
    fieldMap: { manager: 'userName', contact: 'userMobile', warehouse: 'warehouseName', account: 'isActive' },
  });

  const { data: warehouses } = useQuery({ queryKey: ['warehouses-all'], queryFn: () => request<Warehouse[]>({ url: '/warehouses' }) });
  const { data: wmUsers } = useQuery({
    queryKey: ['wm-users'],
    queryFn: () => requestAllPages('/users', { role: 'WAREHOUSE_MANAGER' }),
  });
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['warehouse-assignments', warehouseId, sort],
    queryFn: () => request<Assignment[]>({ url: '/warehouse-assignments', params: { ...(warehouseId ? { warehouseId } : {}), ...(sort ? { sort } : {}) } }),
  });

  /* A warehouse with nobody on it is the one fact this page exists to surface, and the old version
     buried it: you had to read the assignment list and hold the warehouse list in your head to notice
     an absence. The tiles say it outright. They count the WHOLE estate, never the filtered view. */
  const { data: allAssignments } = useQuery({
    queryKey: ['warehouse-assignments', undefined],
    queryFn: () => request<Assignment[]>({ url: '/warehouse-assignments', params: {} }),
  });
  const kpi = useMemo(() => {
    const covered = new Set((allAssignments ?? []).map((a) => String(a.warehouseId)));
    const list = warehouses ?? [];
    return {
      warehouses: list.length,
      managers: new Set((allAssignments ?? []).map((a) => String(a.userId))).size,
      uncovered: list.filter((w) => !covered.has(String(w.id))).length,
      primaries: (allAssignments ?? []).filter((a) => !!a.isPrimary).length,
    };
  }, [allAssignments, warehouses]);
  /* Clicking "Not covered" cannot narrow a list of ASSIGNMENTS to warehouses that have none — there
     is no row to show. It names them instead, under the tile, where the answer belongs. */
  const uncoveredNames = useMemo(() => {
    const covered = new Set((allAssignments ?? []).map((a) => String(a.warehouseId)));
    return (warehouses ?? []).filter((w) => !covered.has(String(w.id)));
  }, [allAssignments, warehouses]);

  /*
   * The assignment row carries only a name, a mobile and a warehouse code. Everything else the reader
   * wants — is there a face to put to the name, can I email them, is the account even active, when
   * did they last sign in, and where is this site — is already in the two lists this page loads for
   * its own dropdowns. Joining them here costs no extra request.
   */
  const userById = useMemo(
    () => new Map<string, any>((wmUsers?.data ?? []).map((u: any) => [String(u.id), u])),
    [wmUsers],
  );
  const whById = useMemo(
    () => new Map<string, Warehouse>((warehouses ?? []).map((w) => [String(w.id), w])),
    [warehouses],
  );

  const invalidate = () => qc.invalidateQueries({ queryKey: ['warehouse-assignments'] });
  const fail = (e: any) => message.error(e.response?.data?.error?.message ?? 'Something went wrong');

  const assignMut = useConfirmedMutation({ title: "Assign this manager to the warehouse?", content: "They get warehouse access and start receiving its low-stock alerts.", okText: "Yes, assign" }, {
    mutationFn: () => request({ url: '/warehouse-assignments', method: 'POST', data: { userId, warehouseId, isPrimary, note: note.trim() || undefined } }),
    onSuccess: () => { invalidate(); setUserId(undefined); setIsPrimary(false); setNote(''); message.success('Manager assigned'); },
    onError: fail,
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => request({ url: `/warehouse-assignments/${id}`, method: 'DELETE' }),
    onSuccess: () => { invalidate(); message.success('Manager removed'); },
    onError: fail,
  });

  const whLabel = (id?: string) => {
    const w = (warehouses ?? []).find((x) => String(x.id) === String(id));
    return w ? `${w.name} (${w.code})` : id;
  };

  const columns = [
    {
      // Each of these cells stacks a couple of fields; the sort follows the one it leads with
      // (see the fieldMap on useServerSort above).
      title: 'Manager', key: 'manager', width: 260, sorter: true,
      render: (_: any, a: Assignment) => {
        const u = userById.get(String(a.userId));
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            {/* The real photo when the account has one; the coloured initials only stand in for it. */}
            <EntityAvatar name={a.userName ?? `#${a.userId}`} src={u?.profilePhotoUrl} size={38} />
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="truncate type-body-strong text-content">{a.userName ?? `#${a.userId}`}</span>
                {/* isPrimary comes from getRawMany() as a 0/1 tinyint — coerce so a 0 doesn't render a stray "0". */}
                {!!a.isPrimary && <Tag tone="gold" icon={<Star size={11} />}>Primary</Tag>}
              </div>
              <div className="mt-0.5"><RoleBadge role="WAREHOUSE_MANAGER" /></div>
            </div>
          </div>
        );
      },
    },
    {
      // Its own column: the row had a whole screen of empty space to its right while the number was
      // squeezed under the name in caption type.
      title: 'Contact', key: 'contact', width: 220, sorter: true,
      render: (_: any, a: Assignment) => {
        const u = userById.get(String(a.userId));
        const mobile = a.userMobile ?? u?.mobile;
        return (
          <div className="min-w-0 space-y-1">
            {mobile ? (
              <a href={`tel:${mobile}`} onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-pill border border-brand-500/25 bg-brand-500/8 px-2 py-0.5 type-label text-brand-700 transition-colors hover:bg-brand-500/15 dark:text-brand-300">
                <Phone size={12} /> {mobile}
              </a>
            ) : <div className="type-caption text-subtle">no number on file</div>}
            {u?.email ? (
              <a href={`mailto:${u.email}`} onClick={(e) => e.stopPropagation()}
                className="flex min-w-0 items-center gap-1.5 type-caption text-sky-600 underline-offset-2 hover:underline dark:text-sky-400">
                <Mail size={11} className="shrink-0" /> <span className="truncate">{u.email}</span>
              </a>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Warehouse', key: 'warehouse', width: 220, sorter: true,
      render: (_: any, a: Assignment) => {
        const w = whById.get(String(a.warehouseId));
        // No mark on this cell. It was an EntityAvatar carrying a CONSTANT warehouse glyph — the same
        // picture on every row, which repeats the column header rather than telling the rows apart.
        // The code, the site name and the MASTER tag already do that.
        return (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center">
              <span className="truncate type-label-strong text-content">{a.warehouseCode ?? a.warehouseName}</span>
              {w?.isMaster ? <MasterTag /> : null}
            </div>
            {a.warehouseCode && a.warehouseName && (
              <div className="truncate type-caption text-subtle">{a.warehouseName}</div>
            )}
          </div>
        );
      },
    },
    {
      // Which site, physically — a manager covering Delhi and Bengaluru is a different fact from one
      // covering two Pune sites, and the code alone never said so.
      title: 'Location', width: 190,
      render: (_: any, a: Assignment) => {
        const w = whById.get(String(a.warehouseId)) as any;
        const city = w?.city;
        const state = w?.stateCode ? (STATE_NAME.get(w.stateCode) ?? w.stateCode) : null;
        if (!city && !state) return <span className="text-subtle">—</span>;
        return (
          <span className="inline-flex min-w-0 items-center gap-1 rounded-pill bg-surface-3 px-2 py-0.5 type-label text-muted">
            <MapPin size={11} className="shrink-0 text-subtle" />
            <span className="truncate">{[city, state].filter(Boolean).join(' · ')}</span>
          </span>
        );
      },
    },
    {
      // An assignment is only worth anything if the account behind it still works — a manager who
      // cannot sign in is an unstaffed warehouse that LOOKS staffed.
      title: 'Account', key: 'account', width: 170, sorter: true,
      render: (_: any, a: Assignment) => {
        const u = userById.get(String(a.userId));
        const active = !u || String(u.status).toLowerCase() === 'active';
        return (
          <div className="min-w-0 space-y-1">
            <Tag tone={active ? 'green' : 'red'} icon={active ? <CircleCheck size={11} /> : <CircleSlash size={11} />}>
              {active ? 'Active' : (u?.status ?? 'Inactive')}
            </Tag>
            <div className="type-caption text-subtle">
              {u?.lastLoginAt ? `last seen ${formatIstRelative(u.lastLoginAt)}` : 'never signed in'}
            </div>
          </div>
        );
      },
    },
    { title: 'Note', dataIndex: 'note', sorter: true, render: (v: string) => (v ? <span className="text-muted">{v}</span> : <span className="text-subtle">—</span>) },
    {
      title: '', align: 'right' as const, width: 60,
      render: (_: any, a: Assignment) => (
        <Popconfirm title="Remove this manager from the warehouse?" onConfirm={() => removeMut.mutate(a.id)} okButtonProps={{ danger: true }}>
          <Button variant="text" size="sm" aria-label={`Remove ${a.userName ?? 'manager'} from ${a.warehouseCode ?? 'this warehouse'}`}
            className="text-muted hover:bg-danger-50 hover:text-danger-700 dark:hover:bg-danger-500/12"
            icon={<Trash2 size={16} />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {!embedded && <Typography.Title level={4}><User size={16} style={{ display: 'inline', verticalAlign: '-2px' }} /> Warehouse Managers</Typography.Title>}
      <Typography.Paragraph type="secondary">
        Assign warehouse managers to the warehouses they operate. A manager only sees and acts on stock, transfers,
        packing and dispatch for their assigned warehouse(s).
      </Typography.Paragraph>

      <div className="mb-3.5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          tone="brand" icon={<UserCheck size={18} />} label="Managers assigned" value={allAssignments ? kpi.managers : '—'}
          hint={`${(allAssignments ?? []).length} assignment${(allAssignments ?? []).length === 1 ? '' : 's'} in all`}
        />
        <StatCard
          tone={kpi.uncovered ? 'warning' : 'neutral'} icon={<TriangleAlert size={18} />} label="Warehouses with nobody"
          value={warehouses && allAssignments ? kpi.uncovered : '—'}
          hint={kpi.uncovered ? uncoveredNames.slice(0, 3).map((w) => w.code).join(', ') + (kpi.uncovered > 3 ? ` +${kpi.uncovered - 3}` : '') : 'every warehouse has a manager'}
        />
        <StatCard
          tone="gold" icon={<Star size={18} />} label="Primary managers" value={allAssignments ? kpi.primaries : '—'}
          hint="the first point of contact for their site"
        />
        <StatCard
          tone="sky" icon={<WarehouseIcon size={18} />} label="Warehouses" value={warehouses ? kpi.warehouses : '—'}
          hint="locations that can be staffed"
        />
      </div>

      <Card title="Assign a manager" className="mb-4">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={7}>
            <Select showSearch optionFilterProp="searchLabel" placeholder="Warehouse" className="w-full"
              value={warehouseId} onChange={setWarehouseId}
              options={(warehouses ?? []).map((w) => ({ value: String(w.id), ...warehouseOption(w) }))} />
          </Col>
          <Col xs={24} md={7}>
            <Select showSearch optionFilterProp="label" placeholder="Warehouse Manager (user)" className="w-full"
              value={userId} onChange={setUserId}
              options={(wmUsers?.data ?? []).map((u: any) => ({ value: String(u.id), label: `${u.fullName} · ${u.mobile}` }))}
              notFoundContent="No users with the Warehouse Manager role" />
          </Col>
          <Col xs={12} md={3}><Space><Switch checked={isPrimary} onCheckedChange={setIsPrimary} /> Primary</Space></Col>
          <Col xs={12} md={5}><Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} /></Col>
          <Col xs={24} md={2}>
            <Button variant="primary" icon={<Plus size={16} />} block disabled={!warehouseId || !userId}
              loading={assignMut.isPending} onClick={() => assignMut.mutate()}>Assign</Button>
          </Col>
        </Row>
      </Card>

      <Card title={<span>Assignments{warehouseId ? ` · ${whLabel(warehouseId)}` : ' (all warehouses)'}</span>}>
        <Table<Assignment> rowKey="id" loading={isLoading} dataSource={assignments ?? []} columns={columns} pagination={false}
          sortState={sortState} onSortChange={onSortChange}
          locale={{ emptyText: <EmptyState title="No managers assigned yet" /> }} />
      </Card>
    </div>
  );
}
